import { Injectable } from '@angular/core';
import { Practice, WebsiteSource } from '@principle-theorem/principle-core';
import type {
  IPractice,
  IWebsiteSnapshot,
  IWebsiteSource,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, isObject, type WithRef } from '@principle-theorem/shared';

/**
 * Reads a practice's stored Website Snapshot, so the import dialog can offer
 * to reuse it instead of reading the website again. It only reads: the
 * importFromWebsite callable is the single writer, and it validates the
 * stored document again before reusing it.
 */
@Injectable({ providedIn: 'root' })
export class AiReceptionistWebsiteSnapshotService {
  /**
   * The latest stored read of `source`, or undefined when there is none. Pass
   * the Source from `WebsiteSource.normaliseUrl(...).source`, as the callable does,
   * so both key the same document. A read the security rules refuse also
   * resolves undefined: a snapshot is only offered for reuse, and the import
   * itself checks practice access on the server.
   */
  async getLatestSnapshot(
    practice: WithRef<IPractice>,
    source: IWebsiteSource
  ): Promise<WithRef<IWebsiteSnapshot> | undefined> {
    const snapshotRef = Firestore.doc(
      Practice.aiReceptionistWebsiteSnapshotsCol(practice),
      WebsiteSource.snapshotDocId(source)
    );
    try {
      return await Firestore.safeGetDoc(snapshotRef);
    } catch (error) {
      if (isObject(error) && error['code'] === 'permission-denied') {
        return;
      }
      throw error;
    }
  }
}
